
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthGuard from '@/components/auth/AuthGuard';
import Header from '@/components/Header';
import PropertyListingForm from '@/components/property/PropertyListingForm';
import ProfileCompletionPrompt from '@/components/account/ProfileCompletionPrompt';
import { useLanguage } from '@/contexts/LanguageContext';
import { checkProfileCompletion } from '@/utils/profileValidation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const ListProperty = () => {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showCompletionPrompt, setShowCompletionPrompt] = useState(false);
  const [completionResult, setCompletionResult] = useState<any>(null);

  // Check for KYC error from URL params
  useEffect(() => {
    const kycError = searchParams.get('kyc_error');
    if (kycError === 'required') {
      toast.error(t('upload_national_id_first', 'يرجى رفع بطاقة الهوية الوطنية أولاً', 'Please upload your national ID first'));
    }
  }, [searchParams, t]);

  useEffect(() => {
    if (profile) {
      if (profile.role === 'student') {
        navigate('/search');
        return;
      }

      // Check KYC verification first
      const hasKYCDocuments = profile?.id_front_url && profile?.id_back_url;
      if (!hasKYCDocuments) {
        // Redirect to account page with error
        navigate('/account?kyc_error=required&redirect=listing');
        return;
      }

      // Check if profile is complete for listing
      const result = checkProfileCompletion(profile, 'listing');
      setCompletionResult(result);
      setShowCompletionPrompt(!result.isComplete);
    }
  }, [profile, navigate]);

  if (profile?.role === 'student') {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4">
            {t('access_denied', 'غير مسموح بالوصول', 'Access Denied')}
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            {t('landlord_only', 'هذه الصفحة متاحة للملاك فقط', 'This page is only available for landlords')}
          </p>
          <button
            onClick={() => navigate('/search')}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary-hover"
          >
            {t('back_to_search', 'العودة للبحث', 'Back to Search')}
          </button>
        </div>
      </div>
    );
  }

  // Show KYC requirement card if documents are missing
  const hasKYCDocuments = profile?.id_front_url && profile?.id_back_url;
  if (!hasKYCDocuments) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto">
            <Card className="border-yellow-200 bg-yellow-50">
              <CardHeader className="text-center">
                <Shield className="h-12 w-12 mx-auto text-yellow-600 mb-4" />
                <CardTitle className="text-xl text-yellow-800">
                  {t('kyc_required_for_listing', 'التحقق من الهوية مطلوب لإدراج العقارات', 'Identity Verification Required for Listing')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="flex items-center gap-2 text-yellow-800 mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium">
                    {t('upload_national_id_first', 'يرجى رفع بطاقة الهوية الوطنية أولاً', 'Please upload your national ID first')}
                  </span>
                </div>
                <p className="text-yellow-700 text-sm">
                  {t('kyc_listing_message', 'يجب رفع مستندات الهوية قبل إدراج العقارات للتأكد من صحة البيانات', 'You must upload identity documents before listing properties to verify your information')}
                </p>
                
                <Button onClick={() => navigate('/account')} className="w-full">
                  {t('upload_documents_now', 'رفع المستندات الآن', 'Upload Documents Now')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show profile completion prompt if profile is incomplete
  if (showCompletionPrompt && completionResult) {
    return (
      <ProfileCompletionPrompt 
        completionResult={completionResult}
        action="listing"
        onClose={() => navigate('/dashboard')}
      />
    );
  }

  return (
    <AuthGuard requireAuth={true}>
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-8 text-center">
              {t('list_property', 'أدرج عقارك', 'List Your Property')}
            </h1>
            <PropertyListingForm />
          </div>
        </div>
      </div>
    </AuthGuard>
  );
};

export default ListProperty;
